/** Formateadores y etiquetas de presentación de Packs de Energía (US-2.4, es-CO). */
import type { EnergyPackStatus } from '@goelectric/shared-types';

/** Etiqueta visible de cada estado del pack. */
export const PACK_STATUS_LABELS: Record<EnergyPackStatus, string> = {
  ACTIVE: 'Activo',
  INACTIVE: 'Inactivo',
  EXPIRED: 'Vencido',
  SOLD_OUT: 'Agotado',
};

/** Descripción breve del estado para tooltips/ayudas. */
export const PACK_STATUS_DESCRIPTIONS: Record<EnergyPackStatus, string> = {
  ACTIVE: 'Disponible para la venta y consulta de la PWA.',
  INACTIVE: 'Baja lógica: el pack no se ofrece, pero conserva su historial.',
  EXPIRED: 'Fuera de vigencia por fecha de fin.',
  SOLD_OUT: 'Sin kWh disponibles para nuevos clientes.',
};


/** Estado visual adicional: `ACTIVE` pero con la fecha de fin ya cumplida. */
export const PACK_OUT_OF_VALIDITY = 'OUT_OF_VALIDITY' as const;

/** Estado que se pinta en la UI (el del backend o el derivado por vigencia). */
export type PackVisualStatus = EnergyPackStatus | typeof PACK_OUT_OF_VALIDITY;

/** Etiqueta visible del estado visual del pack. */
export const PACK_VISUAL_STATUS_LABELS: Record<PackVisualStatus, string> = {
  ...PACK_STATUS_LABELS,
  OUT_OF_VALIDITY: 'Fuera de vigencia',
};

/** Descripción breve del estado visual para tooltips/ayudas. */
export const PACK_VISUAL_STATUS_DESCRIPTIONS: Record<PackVisualStatus, string> = {
  ...PACK_STATUS_DESCRIPTIONS,
  OUT_OF_VALIDITY: 'El pack sigue activo en el backend, pero su fecha de fin ya pasó.',
};

/**
 * Deriva el estado visual sin modificar el estado del backend: un `ACTIVE` con
 * `validTo` vencido se muestra como “Fuera de vigencia”. Sin fecha de fin o con
 * fecha futura se mantiene `ACTIVE`.
 */
export function derivePackVisualStatus(
  status: EnergyPackStatus,
  validTo: string | null | undefined,
  now: Date = new Date(),
): PackVisualStatus {
  if (status !== 'ACTIVE' || !validTo) return status;
  const end = new Date(validTo).getTime();
  if (Number.isNaN(end)) return status;
  return end <= now.getTime() ? PACK_OUT_OF_VALIDITY : status;
}

/** Orden estable de los estados para selectores. */
export const PACK_STATUS_VALUES: readonly EnergyPackStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'EXPIRED',
  'SOLD_OUT',
];

/** Formatea kWh con separador es-CO y sufijo de unidad (`1.250,5 kWh`). */
export function formatKwh(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)} kWh`;
}

/** Formatea un monto (`$ 60.000`, moneda COP por defecto). */
export function formatCop(
  value: number | null | undefined,
  currency = 'COP',
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Un código ISO inválido no debe romper la tabla ni el detalle.
    return `${value} ${currency}`;
  }
}

/** Fecha/hora corta local (es-CO); `—` si falta o es inválida. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Vigencia legible: `desde → hasta` o "Sin fecha de fin". */
export function formatVigencia(
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
): string {
  const from = formatDateTime(validFrom);
  const to = validTo ? formatDateTime(validTo) : 'Sin fecha de fin';
  return `${from} → ${to}`;
}
